import React from 'react'
import { SvgStar } from '@chilipiper/icons/src/Star'
import * as R from 'remeda'

import { Flex, Box } from '../../new/core-components/box'
import { Icon } from '../icon'
import { VerticalDivider } from '../../new/core-components/divider'
import { Text } from '../../new/core-components/text'
import { UnstyledButton } from '../button'

import { Position } from '../tooltip/types'
import { PillContainer, StyledTooltip } from './Pill.styles'
import { RemovePillButton } from './RemovePillButton'

type Props = {
  ['data-test-id']?: string
  description?: string
  descriptionTooltip?: string
  favorite?: boolean
  favoriteTooltip?: string
  hideRemove?: boolean
  icon?: React.ReactNode
  id?: string
  multiple?: boolean
  name: string
  onFavoriteChange?: (name: string) => void
  onRemove?: (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    name: string,
    id?: string
  ) => void
  placement?: Position
}

export const Pill = ({
  name,
  icon,
  id,
  onRemove = R.doNothing(),
  onFavoriteChange,
  multiple,
  favorite,
  favoriteTooltip = '',
  description,
  descriptionTooltip = '',
  hideRemove,
  placement = 'bottom',
  ['data-test-id']: dataTestId,
}: Props) => {
  return (
    <Flex
      h={7}
      w='100%'
      borderRadius={4}
      overflowX='hidden'
      boxShadow='border/on-major'
      bg='bg/list-item-unselected'
    >
      <PillContainer
        pl={multiple ? 0 : 2}
        pr={2}
        boxShadow={hideRemove ? 'none' : 'border/cell'}
        w='100%'
        data-test-id={dataTestId}
        alignItems='center'
        overflowX='hidden'
      >
        {multiple && (
          <>
            <Flex h={7} justifyContent='center' alignItems='center'>
              <StyledTooltip
                tip={favoriteTooltip}
                place={placement}
                popperOptions={{
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      enabled: false,
                    },
                  ],
                }}
                interactive={false}
              >
                <UnstyledButton
                  px={3}
                  h={7}
                  onClick={() => onFavoriteChange?.(name)}
                  data-test-id={`Pill-favorite-pill-${id ?? name}`}
                  data-checked={favorite}
                  disabled={onFavoriteChange === undefined}
                >
                  <Icon
                    icon={SvgStar}
                    w={3}
                    h={3}
                    color={favorite ? 'icon/action-major' : 'icon/indicator'}
                  />
                </UnstyledButton>
              </StyledTooltip>
            </Flex>
            <VerticalDivider mr={3} />
          </>
        )}
        <Flex w='100%' alignItems='center' overflowX='hidden'>
          {icon && (
            <Flex mr={2} w={5} h={5} alignItems='center'>
              {icon}
            </Flex>
          )}

          <StyledTooltip
            tip={descriptionTooltip}
            place='bottom'
            offset={[0, 10]}
            popperOptions={{
              modifiers: [
                {
                  name: 'preventOverflow',
                  enabled: false,
                },
              ],
            }}
            interactive={false}
          >
            <Flex w='100%' alignItems='center'>
              <Text height={4} fontWeight={500}>
                {name}
              </Text>
              {description && (
                <Flex alignItems='center' ml={1} overflowX='hidden'>
                  <Text
                    color='text/body-moderate'
                    whiteSpace='nowrap'
                    textOverflow='ellipsis'
                    overflowX='hidden'
                    height={4}
                  >
                    {description}
                  </Text>
                </Flex>
              )}
            </Flex>
          </StyledTooltip>
        </Flex>
      </PillContainer>
      {hideRemove ? null : (
        <Box h={7} w={7}>
          <RemovePillButton name={name} onRemove={e => onRemove(e, name, id)} />
        </Box>
      )}
    </Flex>
  )
}
